import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { catchError, defer, EMPTY, from, Observable, switchMap, takeUntil, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OidcSessionEndedError, OidcSessionService } from './oidc-session.service';

@Injectable({ providedIn: 'root' })
export class OidcHttpService {
  constructor(private session: OidcSessionService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isProtectedApi(request.url)) return next.handle(request);

    return defer(() => this.session.initialize()).pipe(
      switchMap(() => from(this.session.getToken())),
      switchMap(token => this.sendAuthenticated(request, next, token).pipe(
        catchError((error: HttpErrorResponse) => error.status === 401
          ? this.retryAfterUnauthorized(request, next, token)
          : throwError(() => error)),
      )),
      catchError(error => {
        if (error instanceof OidcSessionEndedError || this.session.busy) return EMPTY;
        if (!(error instanceof HttpErrorResponse)) {
          this.session.reportError('Unable to renew your session. Please try again.');
        }
        return throwError(() => error);
      }),
      takeUntil(this.session.cancel$),
    );
  }

  private retryAfterUnauthorized(request: HttpRequest<unknown>, next: HttpHandler, rejectedToken: string): Observable<HttpEvent<unknown>> {
    return from(this.session.getToken(rejectedToken)).pipe(
      switchMap(token => this.sendAuthenticated(request, next, token).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.session.reportError('The server did not accept your session. Please try again.');
          }
          return throwError(() => error);
        }),
      )),
    );
  }

  private sendAuthenticated(request: HttpRequest<unknown>, next: HttpHandler, token: string | null): Observable<HttpEvent<unknown>> {
    if (!token) return this.signIn();
    if (this.session.busy) return EMPTY;
    return next.handle(this.withToken(request, token));
  }

  private isProtectedApi(value: string): boolean {
    const url = new URL(value, window.location.origin);
    return [environment.apiBaseURL, environment.opogoPlatformApiUrl].some(base => {
      const api = new URL(base, window.location.origin);
      const path = api.pathname.replace(/\/$/, '');
      return url.origin === api.origin && (url.pathname === path || url.pathname.startsWith(path + '/'));
    });
  }

  private signIn(): Observable<never> {
    void this.session.ensureAuthenticated(window.location.pathname + window.location.search + window.location.hash);
    return EMPTY;
  }

  private withToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({ setHeaders: {
      Authorization: `Bearer ${token}`,
      'Api-Version': '1.0',
    } });
  }
}
